import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash, Warning, CheckCircle, Clock, Bell, User, IdentificationBadge, ArrowRight, Camera } from "@phosphor-icons/react";

function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

function certStatusClass(status) {
  if (status === "expired") return "bg-[#FF3B30]/10 border-[#FF3B30]/40 text-[#FF3B30]";
  if (status === "expiring") return "bg-[#FFD600]/10 border-[#FFD600]/40 text-[#FFD600]";
  return "bg-[#1A1A1A] border-[#333] text-white";
}

function CertStatusIcon({ status }) {
  if (status === "expired") return <Warning size={20} weight="fill" />;
  if (status === "expiring") return <Clock size={20} weight="fill" />;
  return <CheckCircle size={20} weight="fill" className="text-[#22c55e]" />;
}

function dotColorByStatus(expired, expiring) {
  if (expired > 0) return "bg-[#FF3B30]";
  if (expiring > 0) return "bg-[#FFD600]";
  return "bg-[#22c55e]";
}

function statusSummaryLabel(expired, expiring) {
  if (expired) return `${expired} scad`;
  if (expiring) return `${expiring} in scad`;
  return "OK";
}

function EmployeeCard({ emp, certCount, ok, expiring, expired, onDelete }) {
  const dotColor = dotColorByStatus(expired, expiring);
  return (
    <Link to={`/personale/dipendenti/${emp.id}`} data-testid={`emp-card-${emp.id}`}
      className="block bg-[#1A1A1A] border border-[#333] rounded-md p-4 hover:border-[#FFD600] transition">
      <div className="flex items-center gap-3">
        {emp.photo ? (
          <img src={emp.photo} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-[#333]" />
        ) : (
          <div className="w-14 h-14 rounded-full bg-[#0A0A0A] border-2 border-[#333] flex items-center justify-center">
            <User size={28} weight="bold" className="text-[#A3A3A3]" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-heading text-xl font-bold uppercase truncate">{emp.name} {emp.surname || ""}</div>
          <div className="text-xs text-[#A3A3A3] uppercase tracking-wider">{emp.role || "—"}</div>
          <div className="text-xs text-[#A3A3A3] mt-1 flex items-center gap-1">
            <span className={`w-2 h-2 rounded-full ${dotColor}`}></span>
            {certCount} attestati · {statusSummaryLabel(expired, expiring)}
          </div>
        </div>
        <ArrowRight size={20} weight="bold" className="text-[#A3A3A3]" />
      </div>
    </Link>
  );
}

export default function Personale() {
  const [tab, setTab] = useState("dipendenti");
  const [employees, setEmployees] = useState([]);
  const [certs, setCerts] = useState([]);
  const [showEmp, setShowEmp] = useState(false);
  const [showCert, setShowCert] = useState(false);
  const [empForm, setEmpForm] = useState({ name: "", surname: "", role: "", phone: "", email: "", photo: "" });
  const [certForm, setCertForm] = useState({ title: "", employee_id: "", expiry_date: "", file_data: "", file_mime: "", notes: "" });

  const load = useCallback(async () => {
    const [e, c] = await Promise.all([api.get("/employees"), api.get("/certificates")]);
    setEmployees(e.data);
    setCerts(c.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const empSummary = (emp_id) => {
    const list = certs.filter((c) => c.employee_id === emp_id);
    return {
      total: list.length,
      ok: list.filter((c) => c.status === "ok").length,
      expiring: list.filter((c) => c.status === "expiring").length,
      expired: list.filter((c) => c.status === "expired").length,
    };
  };

  const onEmpPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const photo = await fileToBase64(f);
    setEmpForm((p) => ({ ...p, photo }));
  };
  const onCertFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const file_data = await fileToBase64(f);
    setCertForm((p) => ({ ...p, file_data, file_mime: f.type }));
  };

  const submitEmp = async (e) => {
    e.preventDefault();
    try {
      await api.post("/employees", empForm);
      toast.success("Dipendente aggiunto"); setShowEmp(false);
      setEmpForm({ name: "", surname: "", role: "", phone: "", email: "", photo: "" });
      load();
    } catch { toast.error("Errore"); }
  };
  const submitCert = async (e) => {
    e.preventDefault();
    try {
      await api.post("/certificates", certForm);
      toast.success("Attestato aggiunto"); setShowCert(false);
      setCertForm({ title: "", employee_id: "", expiry_date: "", file_data: "", file_mime: "", notes: "" });
      load();
    } catch { toast.error("Errore"); }
  };
  const removeCert = async (id) => {
    if (!confirm("Eliminare?")) return;
    await api.delete(`/certificates/${id}`); load();
  };
  const removeEmp = async (id) => {
    if (!confirm("Eliminare il dipendente?")) return;
    await api.delete(`/employees/${id}`); load();
  };

  const sendNotify = async () => {
    try {
      const { data } = await api.post("/certificates/notify");
      if (data.sent) toast.success(`Email inviata: ${data.count} scadenze`);
      else toast.info(data.reason || "Nessuna scadenza");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <Layout>
      <div className="flex justify-between items-start mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-bold">Modulo</div>
          <h1 className="font-heading text-4xl font-black uppercase tracking-tighter leading-none mt-1">Personale</h1>
        </div>
        <button data-testid="notify-btn" onClick={sendNotify}
          className="bg-[#1A1A1A] border-2 border-[#FF3B30]/40 text-[#FF3B30] h-12 px-3 rounded-md font-black uppercase flex items-center gap-1">
          <Bell size={20} weight="bold"/>
        </button>
      </div>

      <div className="bg-[#1A1A1A] rounded-md p-1 flex gap-1 mb-4">
        <button data-testid="tab-dipendenti" onClick={()=>setTab("dipendenti")}
          className={`flex-1 h-12 rounded font-black uppercase tracking-tight text-sm flex items-center justify-center gap-1 transition ${tab === "dipendenti" ? "bg-[#FFD600] text-[#0A0A0A]" : "text-[#A3A3A3]"}`}>
          <User size={18} weight="bold"/> Dipendenti
        </button>
        <button data-testid="tab-attestati" onClick={()=>setTab("attestati")}
          className={`flex-1 h-12 rounded font-black uppercase tracking-tight text-sm flex items-center justify-center gap-1 transition ${tab === "attestati" ? "bg-[#FFD600] text-[#0A0A0A]" : "text-[#A3A3A3]"}`}>
          <IdentificationBadge size={18} weight="bold"/> Attestati
        </button>
      </div>

      {tab === "dipendenti" && (
        <>
          <button data-testid="add-emp-btn" onClick={()=>setShowEmp(true)}
            className="w-full bg-[#FFD600] text-[#0A0A0A] h-14 rounded-md font-black uppercase mb-4 flex items-center justify-center gap-2">
            <Plus size={22} weight="bold"/> Nuovo dipendente
          </button>
          {employees.length === 0 ? (
            <div className="bg-[#1A1A1A] border border-[#333] rounded-md p-8 text-center text-[#A3A3A3]">
              Nessun dipendente. Aggiungi il primo.
            </div>
          ) : (
            <div className="space-y-2">
              {employees.map((emp) => {
                const s = empSummary(emp.id);
                return <EmployeeCard key={emp.id} emp={emp} certCount={s.total} ok={s.ok} expiring={s.expiring} expired={s.expired} onDelete={()=>removeEmp(emp.id)} />;
              })}
            </div>
          )}
        </>
      )}

      {tab === "attestati" && (
        <>
          <button data-testid="add-cert-btn" onClick={()=>setShowCert(true)}
            className="w-full bg-[#FFD600] text-[#0A0A0A] h-14 rounded-md font-black uppercase mb-4 flex items-center justify-center gap-2">
            <Plus size={22} weight="bold"/> Nuovo attestato
          </button>
          {certs.length === 0 ? (
            <div className="bg-[#1A1A1A] border border-[#333] rounded-md p-8 text-center text-[#A3A3A3]">
              Nessun attestato. Aggiungi il primo.
            </div>
          ) : (
            <div className="space-y-3">
              {certs.map((c) => (
                <div key={c.id} className={`border-2 rounded-md p-4 ${certStatusClass(c.status)}`} data-testid={`cert-${c.id}`}>
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <CertStatusIcon status={c.status} />
                        <div className="font-heading text-xl font-bold uppercase text-white">{c.title}</div>
                      </div>
                      {c.employee_name && <div className="text-sm text-white/70">Per: {c.employee_name}</div>}
                      <div className="text-sm mt-1">
                        Scade: <b>{new Date(c.expiry_date).toLocaleDateString("it-IT")}</b>
                        {c.days_to_expiry !== null && (
                          <span className="ml-2 font-mono">({c.days_to_expiry < 0 ? `${-c.days_to_expiry}gg fa` : `tra ${c.days_to_expiry}gg`})</span>
                        )}
                      </div>
                      {c.notes && <div className="text-xs text-white/70 mt-1">{c.notes}</div>}
                    </div>
                    <button onClick={()=>removeCert(c.id)} data-testid={`del-cert-${c.id}`} className="text-[#FF3B30] p-1"><Trash size={20} weight="bold"/></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Add Employee Modal */}
      {showEmp && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center p-2" onClick={()=>setShowEmp(false)}>
          <form onSubmit={submitEmp} className="bg-[#1A1A1A] border border-[#333] rounded-md w-full max-w-lg max-h-[90vh] overflow-auto" onClick={(e)=>e.stopPropagation()}>
            <div className="border-b border-[#333] p-4 font-heading text-xl font-bold uppercase">Nuovo dipendente</div>
            <div className="p-4 space-y-3">
              <div className="flex items-center gap-3">
                {empForm.photo ? <img src={empForm.photo} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-[#333]"/> : <div className="w-16 h-16 rounded-full bg-[#0A0A0A] border-2 border-dashed border-[#333] flex items-center justify-center"><Camera size={24} weight="bold" className="text-[#A3A3A3]"/></div>}
                <label className="flex-1 cursor-pointer text-sm text-[#FFD600] font-bold uppercase">
                  Carica foto
                  <input data-testid="emp-photo" type="file" accept="image/*" onChange={onEmpPhoto} className="hidden"/>
                </label>
              </div>
              <input data-testid="emp-name" required placeholder="Nome" value={empForm.name} onChange={(e)=>setEmpForm({...empForm, name: e.target.value})}
                className="w-full bg-[#0A0A0A] border-2 border-[#333] h-12 rounded-md px-3 focus:border-[#FFD600] focus:outline-none"/>
              <input data-testid="emp-surname" placeholder="Cognome" value={empForm.surname} onChange={(e)=>setEmpForm({...empForm, surname: e.target.value})}
                className="w-full bg-[#0A0A0A] border-2 border-[#333] h-12 rounded-md px-3 focus:border-[#FFD600] focus:outline-none"/>
              <input data-testid="emp-role" placeholder="Ruolo (es. Capo cantiere)" value={empForm.role} onChange={(e)=>setEmpForm({...empForm, role: e.target.value})}
                className="w-full bg-[#0A0A0A] border-2 border-[#333] h-12 rounded-md px-3 focus:border-[#FFD600] focus:outline-none"/>
              <input data-testid="emp-phone" placeholder="Telefono" value={empForm.phone} onChange={(e)=>setEmpForm({...empForm, phone: e.target.value})}
                className="w-full bg-[#0A0A0A] border-2 border-[#333] h-12 rounded-md px-3 focus:border-[#FFD600] focus:outline-none"/>
              <input data-testid="emp-email" type="email" placeholder="Email" value={empForm.email} onChange={(e)=>setEmpForm({...empForm, email: e.target.value})}
                className="w-full bg-[#0A0A0A] border-2 border-[#333] h-12 rounded-md px-3 focus:border-[#FFD600] focus:outline-none"/>
              <div className="flex gap-2">
                <button type="button" onClick={()=>setShowEmp(false)} className="flex-1 bg-[#1A1A1A] border-2 border-[#333] h-12 rounded-md font-black uppercase">Annulla</button>
                <button data-testid="emp-save" type="submit" className="flex-1 bg-[#FFD600] text-[#0A0A0A] h-12 rounded-md font-black uppercase">Salva</button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Add Certificate Modal */}
      {showCert && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center p-2" onClick={()=>setShowCert(false)}>
          <form onSubmit={submitCert} className="bg-[#1A1A1A] border border-[#333] rounded-md w-full max-w-lg max-h-[90vh] overflow-auto" onClick={(e)=>e.stopPropagation()}>
            <div className="border-b border-[#333] p-4 font-heading text-xl font-bold uppercase">Nuovo attestato</div>
            <div className="p-4 space-y-3">
              <input data-testid="cert-title" required placeholder="Titolo (es. Sicurezza Generale)" value={certForm.title} onChange={(e)=>setCertForm({...certForm, title: e.target.value})}
                className="w-full bg-[#0A0A0A] border-2 border-[#333] h-12 rounded-md px-3 focus:border-[#FFD600] focus:outline-none"/>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold mb-1 block">Dipendente</label>
                <select data-testid="cert-emp" value={certForm.employee_id} onChange={(e)=>setCertForm({...certForm, employee_id: e.target.value})}
                  className="w-full bg-[#0A0A0A] border-2 border-[#333] h-12 rounded-md px-3 focus:border-[#FFD600] focus:outline-none">
                  <option value="">— Generico (azienda) —</option>
                  {employees.map((e) => <option key={e.id} value={e.id}>{e.name} {e.surname || ""}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold mb-1 block">Data scadenza</label>
                <input data-testid="cert-expiry" required type="date" value={certForm.expiry_date} onChange={(e)=>setCertForm({...certForm, expiry_date: e.target.value})}
                  className="w-full bg-[#0A0A0A] border-2 border-[#333] h-12 rounded-md px-3 focus:border-[#FFD600] focus:outline-none"/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold mb-1 block">File (PDF/Foto)</label>
                <input data-testid="cert-file" type="file" accept="application/pdf,image/*" onChange={onCertFile}
                  className="w-full text-sm text-[#A3A3A3]"/>
              </div>
              <textarea data-testid="cert-notes" placeholder="Note" value={certForm.notes} onChange={(e)=>setCertForm({...certForm, notes: e.target.value})}
                className="w-full bg-[#0A0A0A] border-2 border-[#333] rounded-md p-3 min-h-[60px] focus:border-[#FFD600] focus:outline-none"/>
              <div className="flex gap-2">
                <button type="button" onClick={()=>setShowCert(false)} className="flex-1 bg-[#1A1A1A] border-2 border-[#333] h-12 rounded-md font-black uppercase">Annulla</button>
                <button data-testid="cert-save" type="submit" className="flex-1 bg-[#FFD600] text-[#0A0A0A] h-12 rounded-md font-black uppercase">Salva</button>
              </div>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}
